"use client";

/**
 * The learner's practice stack (tool + language), e.g. Playwright · TypeScript.
 *
 * Signed-in learners: stored server-side (GET/PUT /users/me/preferences) and cached per user
 * in localStorage for an instant first paint. Guests: stored in localStorage only, and carried
 * over to the account the first time they sign in.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { preferencesApi } from "@/lib/api/endpoints";
import { useSession } from "@/lib/auth/session";
import { parseStack, type StackPreference } from "./stacks";

const GUEST_KEY = "guest";
const storageKey = (owner: string) => `targetqa-stack:${owner}`;

const syncedPreferencesOwners = new Set<string>();

export function resetPreferencesSync(): void {
  syncedPreferencesOwners.clear();
}

function readCached(owner: string): StackPreference | null {
  try {
    const raw = window.localStorage.getItem(storageKey(owner));
    return raw ? parseStack(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function writeCached(owner: string, stack: StackPreference | null): void {
  try {
    if (stack) window.localStorage.setItem(storageKey(owner), JSON.stringify(stack));
    else window.localStorage.removeItem(storageKey(owner));
  } catch {
    // Storage can be unavailable (private mode); the in-memory value still works.
  }
}

interface Snapshot {
  owner: string;
  stack: StackPreference | null;
  /** True once the server (or, for guests, local storage) has answered. */
  settled: boolean;
}

export interface PreferencesValue {
  /** The chosen stack, or null when the learner has not chosen one yet. */
  stack: StackPreference | null;
  /** "loading" until the stored preference is known. */
  status: "loading" | "ready";
  /** True for signed-in learners (preference is saved to their account). */
  synced: boolean;
  save(stack: StackPreference): Promise<void>;
}

const PreferencesContext = createContext<PreferencesValue | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const { status: sessionStatus, user } = useSession();
  const owner = sessionStatus === "authenticated" && user ? user.id : sessionStatus === "anonymous" ? GUEST_KEY : null;
  const [snapshot, setSnapshot] = useState<Snapshot | null>({
    owner: GUEST_KEY,
    stack: null,
    settled: true,
  });

  useEffect(() => {
    if (!owner) return;
    let active = true;
    const cached = readCached(owner);
    const isChallengesListingPage = pathname === "/challenges" || pathname === "/challenges/";
    const alreadySynced = syncedPreferencesOwners.has(owner);

    if (owner === GUEST_KEY && cached === null) {
      return () => {
        active = false;
      };
    }

    // Paint the cached value immediately / asynchronously
    void Promise.resolve().then(() => {
      if (active) {
        setSnapshot((current) =>
          current?.owner === owner && current.settled && current.stack === cached
            ? current
            : {
                owner,
                stack: cached,
                settled: owner === GUEST_KEY || !isChallengesListingPage || alreadySynced,
              }
        );
      }
    });

    if (owner === GUEST_KEY) {
      return () => {
        active = false;
      };
    }

    // Only fetch remote preferences once when loading the /challenges catalog page.
    // Do NOT fire on any other page (intro, profile, challenge workspace, etc.),
    // and do NOT re-fetch if already synced during this session.
    if (!isChallengesListingPage || alreadySynced) {
      return () => {
        active = false;
      };
    }

    syncedPreferencesOwners.add(owner);

    preferencesApi.get().then(
      async (remote) => {
        let stack = parseStack(remote);
        if (!stack) {
          // First sign-in after choosing as a guest: keep the guest choice on the account.
          const guest = readCached(GUEST_KEY);
          if (guest) {
            stack = guest;
            await preferencesApi.save(guest.framework, guest.language).catch(() => undefined);
          }
        }
        writeCached(owner, stack);
        if (active) setSnapshot({ owner, stack, settled: true });
      },
      () => {
        // Offline / API unavailable: fall back to the cached value rather than prompting again.
        if (active) setSnapshot({ owner, stack: cached, settled: true });
      },
    );

    return () => {
      active = false;
    };
  }, [owner, pathname]);

  const save = useCallback(
    async (stack: StackPreference) => {
      if (!owner) throw new Error("Your session is still loading. Try again in a moment.");
      if (owner !== GUEST_KEY) {
        await preferencesApi.save(stack.framework, stack.language);
        syncedPreferencesOwners.add(owner);
      }
      writeCached(owner, stack);
      setSnapshot({ owner, stack, settled: true });
    },
    [owner],
  );

  const value = useMemo<PreferencesValue>(() => {
    const current = snapshot && snapshot.owner === owner ? snapshot : null;
    return {
      stack: current?.stack ?? null,
      status: current?.settled ? "ready" : "loading",
      synced: !!owner && owner !== GUEST_KEY,
      save,
    };
  }, [snapshot, owner, save]);

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesValue {
  const value = useContext(PreferencesContext);
  if (!value) throw new Error("usePreferences must be used inside <PreferencesProvider>");
  return value;
}
